<template>
  <div class="page-container">
    <div class="nav-bar">
      <navigator />
    </div>

    <div class="main-content">
      <div class="header">
        <h1>Projects</h1>
      </div>

      <div class="page-background">
        <div class="projects-panel">
          <div class="panel-header">
            <h2>Game Testing Projects</h2>
            <b-form-input
              v-model="searchTerm"
              placeholder="Search by project, studio or developer"
              class="search-input"
              :style="{ backgroundColor: '#f1f1f1', color: 'black' }"
            ></b-form-input>
          </div>

          <div v-if="isLoading" class="empty-state">Loading projects...</div>
          <div v-else-if="filteredProjects.length === 0" class="empty-state">
            No projects found
          </div>

          <table v-else class="projects-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Studio</th>
                <th>Developer</th>
                <th>Testers</th>
                <th>End Date</th>
                <th class="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="project in filteredProjects" :key="project.gameId">
                <td class="project-name">{{ project.gameName }}</td>
                <td>{{ project.gameStudio }}</td>
                <td>{{ developerName(project.devId) }}</td>
                <td>{{ (project.testerIDs || []).length }}</td>
                <td>{{ formatEndTime(project.endTime) }}</td>
                <td class="actions-column">
                  <b-button
                    class="action-button edit-button"
                    size="sm"
                    @click="openEditModal(project)"
                  >
                    Edit
                  </b-button>
                  <b-button
                    class="action-button notify-button"
                    size="sm"
                    @click="openNotifyModal(project)"
                  >
                    Notify
                  </b-button>
                  <b-button
                    class="action-button delete-button"
                    size="sm"
                    @click="openDeleteModal(project)"
                  >
                    Delete
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <b-modal id="edit-project-modal" title="Edit Project" @ok="saveProject">
      <template #modal-header="{ close }">
        <h5 class="modal-title text-orange">Edit Project</h5>
        <b-button variant="outline-danger" size="sm" @click="close">X</b-button>
      </template>
      <div class="form-row">
        <label>Project Name</label>
        <b-form-input v-model="editForm.gameName"></b-form-input>
      </div>
      <div class="form-row">
        <label>Studio</label>
        <b-form-input v-model="editForm.company"></b-form-input>
      </div>
      <div class="form-row">
        <label>QA Lead</label>
        <b-form-input v-model="editForm.qaLeadName"></b-form-input>
      </div>
      <div class="form-row">
        <label>End Date</label>
        <b-form-input type="datetime-local" v-model="editForm.endTime"></b-form-input>
      </div>
      <div class="form-row">
        <label>Description</label>
        <b-form-textarea v-model="editForm.description" rows="4"></b-form-textarea>
      </div>
      <template #modal-footer="{ cancel, ok }">
        <b-button class="save-button" @click="ok">Save</b-button>
        <b-button variant="outline-secondary" @click="cancel">Cancel</b-button>
      </template>
    </b-modal>

    <b-modal id="notify-project-modal" title="Notify Developer" @ok="sendNotification">
      <template #modal-header="{ close }">
        <h5 class="modal-title text-orange">Notify Developer</h5>
        <b-button variant="outline-danger" size="sm" @click="close">X</b-button>
      </template>
      <p class="notify-target">
        To <strong>{{ developerName(selectedProject.devId) }}</strong>
        about <strong>{{ selectedProject.gameName }}</strong>
      </p>
      <b-form-textarea
        v-model="notifyMessage"
        rows="5"
        placeholder="Type your message to the developer..."
      ></b-form-textarea>
      <template #modal-footer="{ cancel, ok }">
        <b-button class="save-button" :disabled="!notifyMessage.trim()" @click="ok">
          Send
        </b-button>
        <b-button variant="outline-secondary" @click="cancel">Cancel</b-button>
      </template>
    </b-modal>

    <b-modal id="delete-project-modal" title="Confirm Deletion" @ok="confirmDelete">
      <template #modal-header="{ close }">
        <h5 class="modal-title text-danger">Confirm Deletion</h5>
        <b-button variant="outline-danger" size="sm" @click="close">X</b-button>
      </template>
      <p>
        Are you sure you want to delete <strong>{{ selectedProject.gameName }}</strong>?
        This also deletes its timeslots and surveys.
      </p>
      <template #modal-footer="{ cancel, ok }">
        <b-button variant="danger" @click="ok">Delete</b-button>
        <b-button variant="outline-secondary" @click="cancel">Cancel</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import navigator from "../layout/NavBarNew.vue";
import GamesObject from "../../Firestore/GamesObject";
import Chat from "../../Firestore/Chat";
import { sendAdminProjectNotification } from "../../Firestore/NotificationDevTes.js";

export default {
  name: "AdminProjects",
  components: {
    navigator
  },
  data() {
    return {
      userType: window.localStorage.getItem("userType")
        ? parseInt(window.localStorage.getItem("userType"))
        : -1,
      isLoading: false,
      projects: [],
      developers: {},
      searchTerm: "",
      selectedProject: {},
      editForm: {
        gameName: "",
        company: "",
        qaLeadName: "",
        endTime: "",
        description: ""
      },
      notifyMessage: ""
    };
  },
  computed: {
    filteredProjects() {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return this.projects;
      return this.projects.filter(project => {
        return (
          (project.gameName || "").toLowerCase().includes(term) ||
          (project.gameStudio || "").toLowerCase().includes(term) ||
          this.developerName(project.devId)
            .toLowerCase()
            .includes(term)
        );
      });
    }
  },
  mounted() {
    if (window.localStorage.getItem("userType")) {
      this.userType = parseInt(window.localStorage.getItem("userType"));
      this.getProjects();
    } else {
      this.$router.push({ name: "login" });
    }
  },
  methods: {
    getProjects() {
      this.isLoading = true;
      GamesObject.getAllProjects()
        .then(res => {
          this.projects = res.data;
          this.isLoading = false;
          this.loadDevelopers();
        })
        .catch(error => {
          console.log(error);
          this.projects = [];
          this.isLoading = false;
        });
    },
    loadDevelopers() {
      const devIds = [...new Set(this.projects.map(p => p.devId).filter(Boolean))];
      devIds.forEach(devId => {
        if (this.developers[devId]) return;
        Chat.getUserInfo(devId)
          .then(res => {
            this.$set(
              this.developers,
              devId,
              res.data.userName ||
                res.data.nickName ||
                res.data.email ||
                "Unknown developer"
            );
          })
          .catch(() => {
            this.$set(this.developers, devId, "Unknown developer");
          });
      });
    },
    developerName(devId) {
      return this.developers[devId] || "Unknown developer";
    },
    formatEndTime(endTime) {
      if (!endTime) return "Not set";
      const date = new Date(endTime);
      if (isNaN(date.getTime())) return "Not set";
      return date.toLocaleDateString();
    },
    openEditModal(project) {
      this.selectedProject = project;
      const qaLead = project.qaLead || {};
      this.editForm = {
        gameName: project.gameName || "",
        company: project.gameStudio || "",
        qaLeadName: qaLead.name || "",
        endTime: this.toDateTimeInput(project.endTime),
        description: project.description || ""
      };
      this.$bvModal.show("edit-project-modal");
    },
    toDateTimeInput(endTime) {
      if (!endTime) return "";
      const value = endTime.slice(0, 16);
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) ? value : "";
    },
    saveProject() {
      const project = this.selectedProject;
      const updatedData = {
        gameName: this.editForm.gameName,
        company: this.editForm.company,
        description: this.editForm.description,
        qaLead: { ...(project.qaLead || {}), name: this.editForm.qaLeadName }
      };
      if (this.editForm.endTime) {
        updatedData.endTime = this.editForm.endTime + ":00";
      }

      GamesObject.updateProject(project.gameId, updatedData)
        .then(() => {
          this.getProjects();
          this.$snackbar.showSnackbar("Project updated!", "#EED202");
        })
        .catch(error => {
          console.error("Error updating project:", error);
          this.$snackbar.showSnackbar("Could not update project", "#d83b3b");
        });
    },
    openNotifyModal(project) {
      this.selectedProject = project;
      this.notifyMessage = "";
      this.$bvModal.show("notify-project-modal");
    },
    sendNotification(bvModalEvt) {
      if (!this.notifyMessage.trim()) {
        bvModalEvt.preventDefault();
        return;
      }
      sendAdminProjectNotification(
        this.selectedProject.devId,
        this.selectedProject,
        this.notifyMessage
      )
        .then(() => {
          this.$snackbar.showSnackbar("Developer notified!", "#EED202");
        })
        .catch(error => {
          console.error("Error notifying developer:", error);
          this.$snackbar.showSnackbar("Could not notify developer", "#d83b3b");
        });
    },
    openDeleteModal(project) {
      this.selectedProject = project;
      this.$bvModal.show("delete-project-modal");
    },
    confirmDelete() {
      const gameId = this.selectedProject.gameId;
      GamesObject.deleteGame(gameId)
        .then(() => {
          this.projects = this.projects.filter(p => p.gameId !== gameId);
          this.$snackbar.showSnackbar("Project deleted!", "#EED202");
        })
        .catch(error => {
          console.error("Error deleting project:", error);
          this.$snackbar.showSnackbar("Could not delete project", "#d83b3b");
        });
    }
  }
};
</script>

<style scoped>
.page-container {
  display: flex;
  gap: 20px;
  background-color: #f0f0f0;
  min-height: 100vh;
  min-width: 100%;
  width: 100%;
  overflow-x: hidden;
}

.nav-bar {
  width: auto;
  max-width: 220px;
  flex: 0 0 auto;
}

.main-content {
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.header {
  width: 100%;
  display: flex;
  margin-bottom: 30px;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding-top: 10px;
  height: 80px;
  box-shadow: 0 4px 10px -6px grey;
  background-color: white;
}

.header h1 {
  background-color: rgb(255, 255, 255);
  color: #ff7222;
  font-weight: bold;
  height: 80px;
  width: 100%;
}

.page-background {
  display: flex;
  gap: 20px;
  width: 100%;
  min-width: 0;
  padding: 0 25px 25px 25px;
}

.projects-panel {
  background-color: white;
  padding: 20px 30px 30px 30px;
  border-radius: 30px;
  width: 100%;
  min-width: 0;
  overflow-x: auto;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.panel-header h2 {
  color: rgb(2, 2, 2);
  margin: 0;
}

.search-input {
  width: 350px;
}

.empty-state {
  color: #6c757d;
  padding: 40px 0;
  text-align: center;
}

.projects-table {
  width: 100%;
  border-collapse: collapse;
}

.projects-table th {
  text-align: left;
  color: #f97427;
  border-bottom: 2px solid #f97427;
  padding: 10px 12px;
}

.projects-table td {
  color: black;
  border-bottom: 1px solid #e0e0e0;
  padding: 12px;
  vertical-align: middle;
}

.project-name {
  font-weight: bold;
}

.actions-column {
  text-align: right;
  white-space: nowrap;
}

.action-button {
  font-size: 0.75rem;
  padding: 6px 10px;
  border: none;
  margin-left: 5px;
}

.edit-button {
  background-color: #f97427;
  color: white;
}

.edit-button:hover {
  background-color: #d45f1e;
  color: white;
}

.notify-button {
  background-color: #f89d53;
  color: white;
}

.notify-button:hover {
  background-color: #d47d39;
  color: white;
}

.delete-button {
  background-color: #d83b3b;
  color: white;
}

.delete-button:hover {
  background-color: #c50c0c;
}

.form-row {
  margin-bottom: 15px;
}

.form-row label {
  color: #f97427;
  font-weight: bold;
  margin-bottom: 5px;
}

.notify-target {
  color: black;
  margin-bottom: 15px;
}

.text-orange {
  color: #f97427;
}

.save-button {
  background-color: #f97427;
  color: white;
  border: none;
}

.modal-header button {
  margin-left: auto;
}
</style>



// WEBPACK FOOTER //
// src/components/admin-only/AdminProjects.vue